import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { menuSidebar } from '../../utils/animations';
import { MENU_ITEMS } from '../../utils/constants';
import SubMenu from './SubMenu';

const MenuSidebar = ({ isOpen, onClose }) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isSubmenuHovered, setIsSubmenuHovered] = useState(false);
  const navigate = useNavigate();
  const leaveTimeoutRef = useRef(null);

  const handleMenuItemClick = (item) => {
    // Navigate immediately on click
    navigate(item.path);
    onClose();
  };

  const handleServiceClick = (path, sectionId) => {
    navigate(path);
    onClose();
    // Scroll to section after navigation
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleMouseEnterItem = (item) => {
    // Clear any pending timeout
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    setHoveredItem(item);
  };

  const handleMouseLeaveItem = () => {
    // Clear any existing timeout
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
    }
    // Add a delay to allow mouse to reach submenu
    leaveTimeoutRef.current = setTimeout(() => {
      if (!isSubmenuHovered) {
        setHoveredItem(null);
      }
      leaveTimeoutRef.current = null;
    }, 300);
  };

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed top-0 left-0 h-full w-80 bg-primary-beige z-50 shadow-2xl overflow-y-auto"
            variants={menuSidebar}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-6 right-6 text-dark hover:text-primary-brown transition-colors"
            >
              <X size={28} />
            </button>

            {/* Menu Items */}
            <div className="pt-20 px-8">
              <nav>
                <ul className="space-y-6">
                  {/* Main Menu Items */}
                  {MENU_ITEMS.map((item, index) => (
                    <motion.li
                      key={item.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * (index + 1) }}
                      onMouseEnter={() => handleMouseEnterItem(item)}
                      onMouseLeave={handleMouseLeaveItem}
                      className="relative cursor-pointer group"
                    >
                      <button
                        onClick={() => handleMenuItemClick(item)}
                        className="text-2xl font-serif text-dark hover:text-primary-brown transition-colors block py-2 w-full text-left relative z-10"
                      >
                        {item.title}
                      </button>
                    </motion.li>
                  ))}
                </ul>
              </nav>

              {/* About Link - at bottom */}
              <motion.div
                className="absolute bottom-20 left-8 right-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                onMouseEnter={() => {
                  setHoveredItem(null);
                }}
              >
                <Link
                  to="/about"
                  onClick={onClose}
                  className="text-2xl font-serif text-dark hover:text-primary-brown transition-colors block py-2"
                >
                  About
                </Link>
              </motion.div>

              {/* Tagline */}
              <motion.div
                className="absolute bottom-8 left-8 right-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <p className="text-dark-light text-sm italic">
                  Transforming Spaces with Artistry and Precision
                </p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SubMenu - shows on hover */}
      <SubMenu
        item={hoveredItem}
        isVisible={isOpen && hoveredItem !== null}
        onServiceClick={handleServiceClick}
        onMouseEnter={() => {
          // Clear timeout when entering submenu
          if (leaveTimeoutRef.current) {
            clearTimeout(leaveTimeoutRef.current);
            leaveTimeoutRef.current = null;
          }
          setIsSubmenuHovered(true);
        }}
        onMouseLeave={() => {
          setIsSubmenuHovered(false);
          setHoveredItem(null);
        }}
      />
    </>
  );
};

export default MenuSidebar;